import { Answer, AnswerType, ImageView, LogoView } from '@packages/types'
import classNames from 'classnames'
import React from 'react'

import useThemeSettings from 'customizer/theme/useThemeSettings'
import defaultThumbnailImg from 'img/variation.png'
import ColorAnswer from 'themes/common/components/answers/ColorAnswer'
import DescriptionTooltip from 'themes/common/components/answers/DescriptionTooltip'
import SVGAnswer from 'themes/common/components/answers/SVGAnswer'
import ThumbnailBadge from 'themes/common/components/answers/ThumbnailBadge'
import ExtraPriceLabel from 'themes/common/components/ExtraPriceLabel'
import type { AnswerStyle } from 'themes/common/types/style'

import ImageAnswer from './ImageAnswer'
import TextAnswer from './TextAnswer'

type IconProps = ThumbnailAnswerProps & {
  answerStyle: AnswerStyle
}

const AnswerIcon = (props: IconProps) => {
  const { type, thumbnail, answerStyle } = props

  if (thumbnail != null) {
    return <ImageAnswer {...props} value={thumbnail.url || defaultThumbnailImg} answerStyle={answerStyle} />
  }

  switch (type) {
    case AnswerType.Image:
      const imageView = (props.views as ImageView[]).find(({ image }) => image != null)
      return (
        <ImageAnswer
          {...props}
          value={imageView?.image?.url ?? defaultThumbnailImg}
          noResize={!imageView?.image?.url}
          answerStyle={answerStyle}
        />
      )
    case AnswerType.Logo:
      const logoView = (props.views as LogoView[]).find(({ logo }) => logo != null)

      return (
        <ImageAnswer
          {...props}
          value={logoView?.logo?.url ?? defaultThumbnailImg}
          noResize={!logoView?.logo?.url}
          answerStyle={answerStyle}
        />
      )
    case AnswerType.Material:
    case AnswerType.Font:
    case AnswerType.FontSize:
    case AnswerType.Value:
      return <SVGAnswer {...props} answerStyle={answerStyle} />
    case AnswerType.Color:
    case AnswerType.Outline:
      return <ColorAnswer {...props} answerStyle={answerStyle} />
    case AnswerType.Text:
      return <ImageAnswer {...props} value={defaultThumbnailImg} noResize answerStyle={answerStyle} />
    default:
      return null
  }
}

export type ThumbnailAnswerProps = Answer & {
  label?: string
  currency: string
  lang?: string
  country?: string
  extraClassNames?: string
  handleClick: (answerId: string) => void
  selected: boolean
  disabled?: boolean
  showLabel: boolean
  price?: string | number
  inputType: string
  isMultiAnswer?: boolean
  isOutOfStock?: boolean
  isLarge?: boolean
  thumbnailStyle: {
    thumbnailBorderRadius: string
    thumbnailWidth: string
    thumbnailHeight: string
    showMultiAnswerFeedback: boolean
    feedbackIconColor: string
    feedbackBackgroundColor: string
    feedbackBorderWidth: string
    feedbackBorderColor: string
    removeFeedbackIconColor: string
    removeFeedbackBackgroundColor: string
  }
  showRelativePrice?: boolean
}

const ThumbnailAnswer = (props: ThumbnailAnswerProps) => {
  const {
    id,
    selected,
    disabled,
    handleClick = () => {},
    price,
    currency,
    lang,
    country,
    thumbnailStyle,
    showLabel,
    showDescription,
    description,
    name,
    isMultiAnswer,
    isOutOfStock,
    isLarge,
    showRelativePrice,
  } = props

  const answerStyle = useThemeSettings('answer')
  const style = {
    ...answerStyle,
    borderRadius: thumbnailStyle.thumbnailBorderRadius,
    width: thumbnailStyle.thumbnailWidth,
    height: thumbnailStyle.thumbnailHeight,
  }

  const handleAnswerClick = () => !disabled && handleClick(id)

  return (
    <div className={classNames('answer-container', { selected, disabled })} onClick={handleAnswerClick}>
      <ThumbnailBadge
        isLarge={isLarge}
        selected={selected}
        isOutOfStock={isOutOfStock}
        isMultiAnswer={isMultiAnswer && thumbnailStyle.showMultiAnswerFeedback}
      />
      <DescriptionTooltip
        key={`tooltip-${id}`}
        description={description}
        showDescription={showDescription}
        header={name}
        style={answerStyle}
        allowHeaderOnly={!showLabel}
        onClick={handleAnswerClick}
        isOutOfStock={isOutOfStock}
      >
        <div>
          <AnswerIcon {...props} answerStyle={style} />
        </div>
      </DescriptionTooltip>
      <ExtraPriceLabel
        extraClassNames="price-label__under-thumbnail"
        price={price}
        currency={currency}
        lang={lang}
        country={country}
        showRelativePrice={showRelativePrice}
      />
      <TextAnswer {...props} answerStyle={answerStyle} />
    </div>
  )
}

ThumbnailAnswer.displayName = 'ThumbnailAnswer'

export default ThumbnailAnswer
